// Query and Mutation
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

// Http Methods
import { getRequest as get, postRequest as post } from '../utils/HttpMethods';

// Utils
import queryString from 'api/utils/queryStringBuilder';
import { useListQuery, useMutationQuery } from '../utils/queries';

//Interface
import { Diagnosis, isDiagnosis } from 'interfaces';

export const diagnosisQueryKeys = {
  all: ['diagnosis'],
  get: (id: string) => [...diagnosisQueryKeys.all, id],
  favorites: (providerID: string) => [
    ...diagnosisQueryKeys.all,
    'favorites',
    providerID,
  ],
};

/**
 * @description Searches the diagnosis table based on icd9 or description.
 * @param {string} searchValue - The diagnosis data to search on. Can be either description or icd9.
 * @returns {Promise<Diagnosis[]>} A promise that resolves to an array of possible matches for the search.
 */
const searchDiagnosis = async (searchValue: string): Promise<Diagnosis[]> => {
  return get<Diagnosis[]>(
    'QBilling',
    `/resource/diagnosis/search${queryString({ searchValue })}`
  );
};

/**
 * @description Hook to search the diagnosis table based on description or icd9.
 * @param {Object} params - The parameters for the query.
 * @param {string} params.searchValue - he diagnosis data to search on. Can be either description or icd9.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<Diagnosis, Error>} The result of the query.
 */
export const useSearchDiagnosis = ({
  searchValue,
  displayNotification = false,
  enabled = true,
}: {
  searchValue: string;
  displayNotification?: boolean;
  enabled?: boolean;
}): UseQueryResult<Diagnosis[], Error> => {
  return useListQuery<Diagnosis>({
    queryKey: diagnosisQueryKeys.get(searchValue),
    fetchFunction: searchDiagnosis,
    functionName: 'searchDiagnosis',
    params: searchValue,
    isEntity: isDiagnosis,
    enabled: enabled && !!searchValue,
    displayNotification,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

const listProviderDiagnosisFavorites = async (
  providerID: string
): Promise<Diagnosis[]> => {
  return get<Diagnosis[]>(
    'QBilling',
    `/resource/provider/favourite/diagnosis${queryString({ providerID })}`
  );
};

export const useListProviderDiagnosisFavorites = ({
  providerID,
  displayNotification = false,
  enabled = true,
}: {
  providerID: string;
  displayNotification?: boolean;
  enabled?: boolean;
}): UseQueryResult<Diagnosis[], Error> => {
  return useListQuery<Diagnosis>({
    queryKey: diagnosisQueryKeys.favorites(providerID),
    fetchFunction: listProviderDiagnosisFavorites,
    functionName: 'listProviderDiagnosisFavorites',
    params: providerID,
    isEntity: isDiagnosis,
    enabled: !!providerID && enabled,
    displayNotification,
  });
};

const addProviderDiagnosisFavorite = async ({
  providerID,
  diagnosisID,
}: {
  providerID: string;
  diagnosisID: string;
}): Promise<Diagnosis | null> =>
  await post('QBilling', `/resource/provider/favourite/diagnosis`, {
    providerID,
    diagnosisID,
  });

export const useAddProviderDiagnosisFavorite = (): UseMutationResult<
  Diagnosis | null,
  Error
> => {
  return useMutationQuery<Diagnosis | null>({
    mutationFn: addProviderDiagnosisFavorite,
    errorMessage: 'Failed to add diagnosis favorite',
    successMessage: 'Diagnosis favorited',
    queryKey: diagnosisQueryKeys.all,
  });
};

const removeProviderDiagnosisFavorite = async ({
  providerID,
  diagnosisID,
}: {
  providerID: string;
  diagnosisID: string;
}): Promise<boolean | null> =>
  await post('QBilling', `/resource/provider/favourite/diagnosis/remove`, {
    providerID,
    diagnosisID,
  });

export const useRemoveProviderDiagnosisFavorite = (): UseMutationResult<
  boolean | null,
  Error
> => {
  return useMutationQuery<boolean | null>({
    mutationFn: removeProviderDiagnosisFavorite,
    errorMessage: 'Failed to remove diagnosis favorite',
    successMessage: 'Diagnosis favorite removed',
    queryKey: diagnosisQueryKeys.all,
  });
};
